import { useMemo } from 'react';
import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';

import { DOC_RELAYS, KIND_DOC_METADATA } from '@/lib/doc/kinds';
import { parseDocMetadata, toDocSummary } from '@/lib/doc/metadata';
import { getRecentTimestamps } from '@/lib/doc/storage';
import type { DocMetadata, DocSummary } from '@/lib/doc/types';
import { useCurrentUser } from '@/hooks/useCurrentUser';

export interface DocListResult {
  documents: DocSummary[];
  owned: DocSummary[];
  shared: DocSummary[];
  isLoading: boolean;
}

/** Fetch the user's documents (owned + shared with them) from the doc relays. */
export function useDocList(): DocListResult {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const pubkey = user?.pubkey;

  const query = useQuery({
    queryKey: ['nostr', 'doc-list', pubkey ?? 'anon'],
    queryFn: async ({ signal }) => {
      if (!pubkey) return [] as DocSummary[];

      const docNostr = nostr.group([...DOC_RELAYS]);
      const events = await docNostr.query(
        [
          { kinds: [KIND_DOC_METADATA], authors: [pubkey], limit: 100 },
          { kinds: [KIND_DOC_METADATA], '#p': [pubkey], limit: 200 },
        ],
        { signal: signal ?? AbortSignal.timeout(10_000) },
      );

      // Deduplicate per (owner, docId) keeping the latest version.
      const latest = new Map<string, DocMetadata>();
      for (const event of events) {
        const metadata = parseDocMetadata(event);
        if (!metadata || metadata.deleted) continue;
        const key = `${metadata.ownerPubkey}:${metadata.docId}`;
        const existing = latest.get(key);
        if (!existing || metadata.updatedAt > existing.updatedAt) {
          latest.set(key, metadata);
        }
      }

      const recent = getRecentTimestamps(pubkey);
      const summaries = [...latest.values()].map((metadata) => {
        const summary = toDocSummary(metadata, pubkey);
        const localActivity = recent[`${summary.ownerPubkey}:${summary.docId}`];
        if (localActivity && localActivity / 1000 > summary.updatedAt) {
          return { ...summary, updatedAt: Math.floor(localActivity / 1000) };
        }
        return summary;
      });

      return summaries.sort((a, b) => b.updatedAt - a.updatedAt);
    },
    enabled: Boolean(pubkey),
    staleTime: 15_000,
  });

  const documents = useMemo(() => query.data ?? [], [query.data]);

  return {
    documents,
    owned: useMemo(() => documents.filter((doc) => doc.role === 'owner'), [documents]),
    shared: useMemo(() => documents.filter((doc) => doc.role !== 'owner'), [documents]),
    isLoading: query.isLoading && Boolean(pubkey),
  };
}
